import { useState, useEffect, useRef } from 'react';
import { TimeSpan } from '@/utility/time-span.ts';
import { convertMsToClockTime } from '@/utility';
import { useServerTimeStore } from '@/zustand/server-time/store.ts';

interface AppointmentStatusData {
	status: 'COMPLETED' | 'UPCOMING' | 'SCHEDULED' | 'ACTIVE';
	isInOneHour: boolean;
	secondsLeft: number;
	formattedTimeLeft: string;
}

type AppointmentMap = { [appointmentId: number]: AppointmentStatusData };

const formatTime = time => {
	return time.toString().padStart(2, '0');
};

export function useAppointmentStatus(appointments: any[]) {
	const [statusMap, setStatusMap] = useState<AppointmentMap>({});
	const appointmentStartDatesRef = useRef<Record<number, Date>>({});
	const timerTimeoutsRef = useRef<Record<number, NodeJS.Timeout>>({});
	const serverTimeRef = useRef<Date | null>(null);

	const { data: serverTimeData } = useServerTimeStore();

	useEffect(() => {
		if (!appointments || appointments.length === 0) return;

		appointments.forEach(appointment => {
			appointmentStartDatesRef.current[appointment.appointmentId] = new Date(appointment.appointmentStartDate);
		});

		appointments.forEach(appointment => {
			calculateRemainingTime(appointment.appointmentId);
		});

		return () => {
			Object.values(timerTimeoutsRef.current).forEach(timeout => {
				clearTimeout(timeout);
			});
			timerTimeoutsRef.current = {};
		};
	}, [appointments]);

	useEffect(() => {
		serverTimeRef.current = serverTimeData?.serverTime || new Date();
	}, [serverTimeData]);

	const calculateRemainingTime = (appointmentId: number) => {
		if (!appointmentStartDatesRef.current[appointmentId]) return;

		const appointment = appointments.find(a => a.appointmentId === appointmentId);
		if (!appointment) return;

		const now = new Date(serverTimeRef.current).getTime();
		const appointmentTime = appointmentStartDatesRef.current[appointmentId].getTime();
		const appointmentEndTime = appointment.appointmentEndDate ? new Date(appointment.appointmentEndDate).getTime() : appointmentTime + TimeSpan.minutes.thirty;

		const diff = appointmentTime - now;

		let status: 'COMPLETED' | 'UPCOMING' | 'SCHEDULED' | 'ACTIVE';
		let isInOneHour = false;
		const secondsLeft = Math.floor(diff / 1000);
		let formattedTimeLeft = '';

		if (now > appointmentEndTime) {
			status = 'COMPLETED';
		} else if (now > appointmentTime && now < appointmentEndTime) {
			status = 'ACTIVE';
		} else if (diff <= TimeSpan.minutes.five && diff > 0) {
			const currentStatus = statusMap[appointmentId]?.status;
			if (currentStatus === 'ACTIVE' && diff < 1000) {
				status = 'ACTIVE';
			} else {
				status = 'UPCOMING';
			}
		} else {
			status = 'SCHEDULED';
		}

		isInOneHour = diff <= TimeSpan.hours.one && diff > 0;

		if (diff <= 0) {
			formattedTimeLeft = '00:00 sn';
		} else {
			const time = convertMsToClockTime(diff);
			if (time.hours > 0) {
				formattedTimeLeft = `${formatTime(time.hours)}:${formatTime(time.minutes)}:${formatTime(time.seconds)} sn`;
			} else {
				formattedTimeLeft = `${formatTime(time.minutes)}:${formatTime(time.seconds)} sn`;
			}
		}

		setStatusMap(prevMap => ({
			...prevMap,
			[appointmentId]: {
				status,
				isInOneHour,
				secondsLeft,
				formattedTimeLeft
			}
		}));

		if (timerTimeoutsRef.current[appointmentId]) {
			clearTimeout(timerTimeoutsRef.current[appointmentId]);
		}

		timerTimeoutsRef.current[appointmentId] = setTimeout(() => {
			calculateRemainingTime(appointmentId);
		}, TimeSpan.seconds.one);
	};

	return statusMap;
}
